import React, { useState, useEffect } from "react";
import "./ProductColor.css";
import { Button } from "antd";
import { fabric } from "fabric";
import sign from "../assets/nofill.png";

const hexToRgba = (hex, alpha = 1) => {
  let r = 0, g = 0, b = 0;

  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }

  return { r, g, b, a: alpha };
};

// Convert Blob -> data URL (so the image source is stable and won't be revoked)
const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });

const ImagePaletteBox = ({
  onColorChange,
  excludeNoFill,
  onClose,
  setSingleColor,
  originalImagePalette = [],   // frozen
  currentPalette = [],         // live/current
  dominantColorBGRA = null,
  removeBackground = false,
  onPickOriginalIndex,
  selectedOriginalIndex,     // ✅ NEW
}) => {
  const [currentColor, setCurrentColor] = useState(null);
  const [hoveredColorName, setHoveredColorName] = useState("");
  const [selectedColorName, setSelectedColorName] = useState("");
  const [preDefinedColors, setPreDefinedColors] = useState([]);
  const [colorData, setColorData] = useState([]);
  const [lastOpaqueColor, setLastOpaqueColor] = useState(null);

  useEffect(() => {
    fetch("https://backend.inkox.com/api/inkcolors")
      .then((response) => response.json())
      .then((data) => {
        const allColors = data
          .map((item) => item.color_hex.split(","))
          .flat()
          .map((color) => color.trim());
        setColorData(data);

        const rgbaColors = allColors.map((hex) => hexToRgba(hex));
        const noFillColor = { r: 12, g: 20, b: 20, a: 0 };

        const combinedColors = excludeNoFill ? rgbaColors : [noFillColor, ...rgbaColors];
        setPreDefinedColors(combinedColors);
      })
      .catch((error) => {
        console.error("Error fetching outline colors:", error);
      });
  }, [excludeNoFill]);

  const getColorNameFromHex = (hex) => {
    if (hex === "#222" || hex === "transparent") return "No Fill";
    for (const item of colorData) {
      const colorHexArray = item.color_hex.split(",").map((color) => color.trim());
      const colorNameArray = item.color_name.split(",").map((name) => name.trim());
      const index = colorHexArray.indexOf(hex);
      if (index !== -1) {
        return colorNameArray[index] || "";
      }
    }
    return "";
  };

  const rgbToHex = (color) => {
    if (!color || color.r === undefined || color.g === undefined || color.b === undefined) {
      return "#000000";
    }
    if (color.a === 0) return "transparent";

    const { r, g, b } = color;
    const toHex = (value) => {
      const hex = value.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const handleColorChangeLocal = (color) => {
    const hexColor = rgbToHex(color);
    const colorName = getColorNameFromHex(hexColor);
    setCurrentColor(color);
    setSelectedColorName(colorName);

    if (color.isNoFill && lastOpaqueColor) {
      const transparentVersion = { ...lastOpaqueColor, a: 0 };
      onColorChange(transparentVersion);
      setSingleColor(transparentVersion);
    } else {
      onColorChange(color);
      setSingleColor(color);
      if (color.a !== 0) {
        setLastOpaqueColor(color);
      }
    }
  };
  // BGRA array -> rgba object {r,g,b,a 0..1}
const bgraToRgbaObj = (bgra) => {
  if (!Array.isArray(bgra)) return null;
  const [b, g, r, a = 255] = bgra;
  return { r, g, b, a: a / 255 };
};


// BGRA array -> "rgba(r,g,b,a)" string (for inline styles)
const bgraToCssRgba = (bgra) => {
  if (!Array.isArray(bgra)) return 'rgba(0,0,0,1)';
  const [b, g, r, a = 255] = bgra;
  return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
};

// Compare dominant vs a palette color (RGB only, tolerance)
const rgbArrClose = (rgb1, rgb2, tol = 2) => {
  if (!rgb1 || !rgb2) return false;
  return (
    Math.abs(rgb1[0] - rgb2[0]) <= tol &&
    Math.abs(rgb1[1] - rgb2[1]) <= tol &&
    Math.abs(rgb1[2] - rgb2[2]) <= tol
  );
};


  const handleColorHover = (color) => {
    const hexColor = rgbToHex(color);
    const colorName = getColorNameFromHex(hexColor);
    setHoveredColorName(colorName);
  };

  const handleHoverOut = () => setHoveredColorName("");

  // -------- Reset (preview original image + original palette) --------
  const previewResetToOriginal = async () => {
    try {
      const canvas = window._canvas || window.canvas; // whichever you set globally
      if (!canvas) return;

      const obj = canvas.getActiveObject();
      if (!obj || obj.type !== "image") return;

      // Resolve ORIGINAL blob with fallbacks
      let originalBlob = obj.originalImageBlob;
      if (!(originalBlob instanceof Blob)) {
        if (obj.imageFile instanceof Blob) {
          originalBlob = obj.imageFile;
        } else if (obj.originalImagePath) {
          const res = await fetch(obj.originalImagePath, { mode: "cors" });
          originalBlob = await res.blob();
        } else {
          const src =
            obj.originalSrc ||
            obj.newImagePath ||
            (typeof obj.getSrc === "function" ? obj.getSrc() : obj.src);
          if (src) {
            const res = await fetch(src, { mode: "cors" });
            originalBlob = await res.blob();
          }
        }
      }
      if (!(originalBlob instanceof Blob)) {
        console.warn("No original blob available to preview reset.");
        return;
      }

      // Use a data URL for stability (won't be revoked)
      const dataURL = await blobToDataURL(originalBlob);

      // Keep stacking & clip
      const zIndex = canvas.getObjects().indexOf(obj);
      const clipPath = obj.clipPath || null;

      fabric.util.loadImage(
        dataURL,
        (el) => {
          const img2 = new fabric.Image(el, {
            left: obj.left,
            top: obj.top,
            angle: obj.angle,
            scaleX: obj.scaleX,
            scaleY: obj.scaleY,
            flipX: obj.flipX,
            flipY: obj.flipY,
            opacity: obj.opacity,
            id: obj.id,
            type: "image",
            // interaction flags (preserve)
            selectable: typeof obj.selectable === "boolean" ? obj.selectable : true,
            evented: typeof obj.evented === "boolean" ? obj.evented : true,
            objectCaching: typeof obj.objectCaching === "boolean" ? obj.objectCaching : true,
            hasBorders: typeof obj.hasBorders === "boolean" ? obj.hasBorders : true,
            hasControls: typeof obj.hasControls === "boolean" ? obj.hasControls : true,
            perPixelTargetFind: !!obj.perPixelTargetFind,
            // custom/meta fields
            session_id: obj.session_id,
            designElement: obj.designElement,
            imageSmoothingEnabled: true,
            imageSmoothingQuality: "high",
            originKey: obj.originKey || obj.id || obj.src,
            imageFile: obj.imageFile,
            imageType: obj.imageType,
            quantized_image: obj.quantized_image,
            selectedColorIndex: obj.selectedColorIndex,
          });

          // restore palette + originals
          const originalPalette =
            (Array.isArray(obj.originalPalette) && obj.originalPalette.length && obj.originalPalette) ||
            obj.paletteColors ||
            [];

          img2.originalImageBlob = originalBlob;
          img2.originalPalette = originalPalette;
          img2.paletteColors = originalPalette;

          // restore clip if any
          if (clipPath) img2.clipPath = clipPath;

          // clear filters for true original preview
          img2.filters = [];
          img2.applyFilters();

          // Replace at SAME index so front/back works predictably
          canvas.remove(obj);
          canvas.insertAt(img2, Math.max(0, zIndex), true);
          canvas.setActiveObject(img2);
          img2.setCoords();
          canvas.requestRenderAll();

          // reset local picker UI state
          setCurrentColor(null);
          setSelectedColorName("");
          setHoveredColorName("");
        },
        null,
        "anonymous"
      );
    } catch (err) {
      console.error("Reset preview failed:", err);
    }
  };
// Build mini strip segments for the full original palette
const OneSwatchBar = ({ bgra }) => {
  if (!Array.isArray(bgra)) return null;
  const [b, g, r, a = 255] = bgra;
  const css = `rgba(${r}, ${g}, ${b}, ${a / 255})`;
  const isTransparent = a === 0;
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 6,
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        overflow: "hidden",
        pointerEvents: "none",
        background: css,
      }}
      title="Original swatch"
    >
      {isTransparent && (
        <img
          src={sign}
          alt="no fill"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.75 }}
        />
      )}
    </div>
  );
};
const OriginalChip = ({ bgra }) => {
  if (!Array.isArray(bgra)) return null;
  const [b, g, r, a = 255] = bgra;
  const css = `rgba(${r}, ${g}, ${b}, ${a / 255})`;
  const isTransparent = a === 0;
  return (
    <div
      style={{
        position: "absolute",
        top: 2,
        right: 2,
        width: 7,
        height: 7,
        borderRadius: 0,
        border: "1px solid rgba(0,0,0,.25)",
        background: css,
        overflow: "hidden",
        pointerEvents: "none",
      }}
      title="Original color"
    >
      {isTransparent && (
        <img
          src={sign}
          alt="no fill"
          style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.8 }}
        />
      )}
    </div>
  );
};

return (
  <div className="color-picker colorpickertwodiv">
    

    {/* ✅ IMAGE PALETTE PREVIEW (ON TOP) */}
{Array.isArray(currentPalette) && currentPalette.length > 0 && (
  <>
    <label>Image Palette</label>
    <div className="color-grid">
      {currentPalette.map((currBGRA, idx) => {
        const [cb, cg, cr, ca = 255] = currBGRA || [];
        const currCss = `rgba(${cr}, ${cg}, ${cb}, ${ca / 255})`;

        // the original color for this index (fallback to current if missing)
        const origBGRA = originalImagePalette[idx] || currBGRA;

        const isTransparent = ca === 0;
        const isDominant =
          removeBackground &&
          Array.isArray(dominantColorBGRA) &&
          Math.abs(dominantColorBGRA[2] - cr) <= 2 &&
          Math.abs(dominantColorBGRA[1] - cg) <= 2 &&
          Math.abs(dominantColorBGRA[0] - cb) <= 2;

        return (
          <div key={`imgpal-${idx}`} className="color-box-container">
            <div
              className={`color-box ${selectedOriginalIndex === idx ? "selected-original" : ""}`}
              style={{ position: "relative", backgroundColor: currCss }}
              onClick={() => {
                // select this slot; NO recolor here
                setCurrentColor({ r: cr, g: cg, b: cb, a: ca / 255 });
                onPickOriginalIndex?.(idx);
              }}
            >
              {/* 🔹 tiny original-color chip (top-right) */}
              <OriginalChip bgra={origBGRA} />

              {isDominant && <span className="cross-icon">&#10060;</span>}
              {isTransparent && (
                <div className="transparent-overlay">
                  <img src={sign} alt="no fill" style={{ height: "100%", width: "100%" }} />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
    <div style={{ height: 8 }} />
  </>
)}



    {/* (existing “currentColor” preview) */}
    {/* {currentColor && (
      <div className="original-color-box">
        <label>Original Selected Color</label>
        <div
          className="color-box original"
          style={{
            backgroundColor: `rgba(${currentColor.r}, ${currentColor.g}, ${currentColor.b}, ${currentColor.a})`,
          }}
        >
          {currentColor.a === 0 && (
            <img src={sign} alt="sign" style={{ height: "100%", width: "100%" }} />
          )}
        </div>
      </div>
    )} */}

    {/* (existing predefined grid) */}
    <label>Select Image Color</label>
    <div className="color-grid">
      {preDefinedColors.map((color, index) => (
        <div
          key={index}
          className={`color-box ${
            currentColor && rgbToHex(color) === rgbToHex(currentColor) ? "selected" : ""
          }`}
          style={{
            backgroundColor: `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`,
          }}
          onClick={() => handleColorChangeLocal(color)}
          onMouseEnter={() => handleColorHover(color)}
          onMouseLeave={handleHoverOut}
        >
          {currentColor && rgbToHex(color) === rgbToHex(currentColor) && (
            <span className="tick-mark">&#10003;</span>
          )}
          {color.a === 0 && (
            <img src={sign} alt="sign" style={{ height: "100%", width: "100%" }} />
          )}
        </div>
      ))}
    </div>

    {/* (rest unchanged) */}
    <b
      className="color-preview"
      style={{ visibility: hoveredColorName || selectedColorName ? "visible" : "hidden" }}
    >
      Color Name: {hoveredColorName || selectedColorName}
    </b>

    <div className="flex-uploadimage">
      <span>
        <Button className="px-5 mt-3 me-3 button-to-texts" onClick={onClose}>
          Done
        </Button>
      </span>
      <span>
        <Button className="px-5 mt-3 me-3 button-to-texts" onClick={previewResetToOriginal}>
          Reset
        </Button>
      </span>
    </div>
  </div>
);

};

export default ImagePaletteBox;
